import { type KeyboardEvent, memo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { IconLayoutKanban, IconList, IconMap2 } from '@tabler/icons-react';

import { AppTooltip } from '@/components/ui/AppTooltip';
import { VIEW_TOGGLE_ICON_SIZE } from '@/constants/ui';
import { useMapViewMode } from '@/hooks/useMapViewMode';
import { VIEW_MODE, NOTE_VIEW_MODE, type ViewMode } from '@/types';

import styles from './ViewToggle.module.css';

const VIEW_OPTIONS: ReadonlyArray<{
  mode: ViewMode;
  icon: typeof IconMap2;
  labelKey: string;
}> = [
  { mode: VIEW_MODE.MAP, icon: IconMap2, labelKey: 'taskViews.toggle.map' },
  { mode: VIEW_MODE.LIST, icon: IconList, labelKey: 'taskViews.toggle.list' },
  {
    mode: VIEW_MODE.KANBAN,
    icon: IconLayoutKanban,
    labelKey: 'taskViews.toggle.kanban',
  },
];

const NOTE_VISIBLE_MODES: ReadonlySet<string> = new Set(
  Object.values(NOTE_VIEW_MODE)
);

interface ViewToggleProps {
  readonly className?: string;
  /**
   * Controlled value — when provided, overrides the URL-derived view.
   * Leave unset to let ViewToggle sync with the `/map` route.
   */
  readonly value?: string;
  /**
   * Controlled onChange — when provided, overrides the URL setter.
   * Leave unset to let ViewToggle write directly to `?view=`.
   */
  readonly onChange?: (mode: string) => void;
  /**
   * Filter which modes to show. When not set, the filter is auto-computed
   * from the current background artifact (`notes` → map+list, otherwise
   * all three).
   */
  readonly visibleModes?: string[];
}

export const ViewToggle = memo(function ViewToggle({
  className,
  value,
  onChange,
  visibleModes,
}: ViewToggleProps) {
  const { t } = useTranslation();
  const { view: urlView, setView, artifact } = useMapViewMode();
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // URL is the source of truth; prop `value` can override (legacy control).
  const activeMode = value ?? urlView;

  // Auto-filter modes when notes are active (notes do not support kanban).
  const effectiveVisibleModes =
    visibleModes ??
    (artifact === 'notes'
      ? VIEW_OPTIONS.filter((o) => NOTE_VISIBLE_MODES.has(o.mode)).map(
          (o) => o.mode
        )
      : undefined);

  const filteredOptions = effectiveVisibleModes
    ? VIEW_OPTIONS.filter((option) =>
        effectiveVisibleModes.includes(option.mode)
      )
    : VIEW_OPTIONS;

  const handleModeChange = useCallback(
    (mode: ViewMode) => {
      if (onChange) {
        onChange(mode);
      } else {
        setView(mode);
      }
    },
    [onChange, setView]
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>) => {
      const currentIndex = buttonRefs.current.findIndex(
        (ref) => ref === event.currentTarget
      );

      if (currentIndex === -1) return;

      let nextIndex = -1;

      if (event.key === 'ArrowRight') {
        nextIndex = (currentIndex + 1) % filteredOptions.length;
      } else if (event.key === 'ArrowLeft') {
        nextIndex =
          (currentIndex - 1 + filteredOptions.length) % filteredOptions.length;
      }

      if (nextIndex !== -1) {
        event.preventDefault();
        buttonRefs.current[nextIndex]?.focus();
      }
    },
    [filteredOptions.length]
  );

  const containerClassName = className
    ? `${styles.container} ${className}`
    : styles.container;

  return (
    <div
      role="tablist"
      aria-label={t('taskViews.toggle.label')}
      className={containerClassName}
    >
      {filteredOptions.map((option, index) => {
        const isActive = activeMode === option.mode;
        const buttonClassName = isActive
          ? `${styles.button} ${styles.active}`
          : styles.button;
        const IconComponent = option.icon;

        return (
          <AppTooltip key={option.mode} labelKey={option.labelKey}>
            <button
              ref={(element) => {
                buttonRefs.current[index] = element;
              }}
              role="tab"
              aria-selected={isActive}
              tabIndex={isActive ? 0 : -1}
              className={buttonClassName}
              onClick={() => handleModeChange(option.mode)}
              onKeyDown={handleKeyDown}
            >
              <IconComponent size={VIEW_TOGGLE_ICON_SIZE} />
            </button>
          </AppTooltip>
        );
      })}
    </div>
  );
});
